
import React from 'react';
import { cn } from '@/lib/utils';
import { useTheme } from "@/components/theme/ThemeProvider";
import { useIsMobile } from '@/hooks/use-mobile';
import { scheduleHeaderHeigth, scheduleHeaderHeigthMobile, scheduleSlotsHeigth, getScheduleSlotsHeightMobile, ProcessedSlot, getWeeklySlotHeight, getWeeklyHeaderHeight } from '@/utils/scheduleUtils';

interface WeeklyTimeSlotColumnProps {
  processedSlots: ProcessedSlot[];
  slotDuration: number;
  compact?: boolean;
}

const GAP_HEIGHT = 30; // Hauteur fixe pour les indicateurs de gap

export const WeeklyTimeSlotColumn: React.FC<WeeklyTimeSlotColumnProps> = ({ processedSlots, slotDuration, compact = false }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const isMobile = useIsMobile();
  const slotHeight = getWeeklySlotHeight(isMobile, compact, slotDuration);
            
  
  return (
    <div className="flex flex-col">
      {/* Header cell to align with day headers */}
      <div className={cn(
        "border-b flex items-center justify-center",
        isMobile ? "w-[26px]" : "w-[55px]",
        isDarkMode ? "border-muted bg-card" : "border-gray-200 bg-white"
      )}
       style={{ height: getWeeklyHeaderHeight(isMobile, compact) }}
>
        <span className={cn(
          "font-medium", 
          isMobile ? "text-[7px]" : "text-xs",
          isDarkMode ? "text-muted-foreground" : "text-gray-500"
        )}>
          {isMobile ? "H" : "Time"}
        </span>
      </div>
      
      {/* Time slots and gaps */}
      {processedSlots.map((slot, idx) => {
        if (slot.type === 'gap') {
          // Rendu du gap grisé avec plage horaire
          return (
            <div
              key={`gap-${slot.startTime}-${idx}`}
              className={cn(
                "border-b flex items-center justify-center",
                isMobile ? "w-[26px] px-0" : "w-[55px] px-1",
                isDarkMode 
                  ? "border-muted/30 bg-muted/20" 
                  : "border-gray-200/50 bg-gray-100/80"
              )}
              style={{ height: GAP_HEIGHT }}
            >
              {isMobile ? (
                <span className={cn(
                  "text-[6px] font-medium",
                  isDarkMode ? "text-muted-foreground/50" : "text-gray-400"
                )}>
                  ···
                </span>
              ) : (
                <span className={cn(
                  "text-[8px] font-medium whitespace-nowrap",
                  isDarkMode ? "text-muted-foreground/60" : "text-gray-400"
                )}>
                  {slot.startTime.substring(0, 5)}
                </span>
              )}
            </div>
          );
        }

        // Rendu normal du créneau horaire
        const timeSlot = slot.time;
        return (
          <div
            key={`slot-${timeSlot}-${idx}`}
            className={cn(
              "border-b flex items-center justify-center",
              isMobile ? "w-[26px] px-0" : "w-[55px] px-1",
              isDarkMode ? "border-muted/50 bg-card" : "border-gray-100 bg-white"
            )}
             style={{ height: slotHeight }}
            
          >
            {isMobile ? (
              <div className="flex flex-col items-center justify-center leading-none">
                <span className={cn(
                  "font-bold text-[8px]",
                  isDarkMode ? "text-muted-foreground" : "text-gray-700"
                )}>
                  {timeSlot.substring(0, 2)}
                </span>
                <span className={cn(
                  "font-bold text-[8px]",
                  isDarkMode ? "text-muted-foreground/70" : "text-gray-500"
                )}>
                  {timeSlot.substring(3, 5)}
                </span>
              </div>
            ) : (
              <span className={cn(
                "font-medium leading-none text-[10px]",
                isDarkMode ? "text-muted-foreground" : "text-gray-600"
              )}>
                {timeSlot}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
